import { Observable } from 'rxjs';
import { authorizedFetch } from '../core/authorizedFetch';
import i18n from '../i18n';

const BASE_URL = process.env.REACT_APP_AI_ASSISTANT_BACKEND_URL;

export interface StreamMessageParams {
  conversationId: string;
  question: string;
  files: File[] | null;
  searchInternet?: boolean;
  retry?: boolean;
}

export class StreamingService {
  createMessageStream(params: StreamMessageParams): Observable<string> {
    const { conversationId, question, files, searchInternet, retry } = params;

    return new Observable<string>((subscriber) => {
      if (!conversationId || String(conversationId) === 'undefined' || String(conversationId) === 'null') {
        subscriber.error(new Error('Invalid conversationId: ' + conversationId));
        return;
      }

      const currentLanguage = i18n.language || 'pl';
      let url = `${BASE_URL}/chat?question=${encodeURIComponent(question)}&conversationId=${conversationId}&lang=${currentLanguage}`;
      if (searchInternet) url += `&searchInternet=${searchInternet}`;
      if (retry) url += `&retry=${retry}`;

      const formData = new FormData();
      if (files && files.length > 0) {
        files.forEach((file) => formData.append('files', file));
      }

      const controller = new AbortController();
      let readerReleased = false;

      const releaseReader = (reader: ReadableStreamDefaultReader<Uint8Array> | null) => {
        if (readerReleased || !reader) return;
        readerReleased = true;
        try {
          reader.releaseLock();
        } catch (e) {
          console.error(e)
        }
      };

      const startStream = async () => {
        try {
          const response = await authorizedFetch(url, {
            method: 'POST',
            headers: {
              Accept: 'text/event-stream',
            },
            body: formData,
            signal: controller.signal,
          });

          if (!response.ok || !response.body) {
            throw new Error('Błąd połączenia ze strumieniem');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          try {
            let reading = true;
            while (reading) {
              const { done, value } = await reader.read();
              if (done) {
                reading = false;
                break;
              }

              // Todo: Refactor or delete! This causes format failure as it mimics Markdown instead of combining
              //  markdown chunks to one string.
              buffer += decoder.decode(value, { stream: true });

              let idx: number;
              while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const rawEvent = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 2);

                const lines = rawEvent.split(/\r?\n/);
                let dataPayload = '';
                for (const line of lines) {
                  const m = line.match(/^data:(.*)$/);
                  if (m) dataPayload += m[1];
                }

                if (dataPayload === '') {
                  subscriber.next('\n\n');
                } else if (dataPayload === '_END_') {
                  releaseReader(reader);
                  subscriber.complete();
                  return;
                } else {
                  subscriber.next(dataPayload);
                }
              }
            }

            releaseReader(reader);
            subscriber.complete();
          } catch (error) {
            releaseReader(reader);
            if (!controller.signal.aborted) {
              subscriber.error(error);
            }
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            subscriber.error(error);
          }
        }
      };

      startStream();

      return () => {
        controller.abort();
      };
    });
  }
}

export const streamingService = new StreamingService();
